<template>
    <div id="app" aria-live="polite">
        <nav-bar v-if="showNav" />
        <div
            id="notification"
            :class="{
                active: !!notification.type,
                [notification.type]: true,
                filler: $route.name === 'FillForm',
                'show-close': notification.showClose,
            }"
            role="alert"
        >
            <div class="inner">
                {{ notification.message }}
                <i class="far fa-times clear-notification" @click="$store.dispatch('notification/clear')"></i>
            </div>
        </div>
        <div id="main" :class="mainClasses">
            <router-view />
            <div
                id="loading"
                class="fast"
                :class="{
                    'left-bar': ['FormLibrary', 'Boxes', 'DataElementsList'].includes($route.name),
                    'full-screen': fullScreenLoading,
                }"
                v-show="loading"
                role="alert"
                :aria-label="loadingMessage ? null : 'Loading'"
            >
                <div class="outer">
                    <div class="content">
                        <loading-spinner :color="!fullScreenLoading ? variables.colorMain : undefined" />
                        <div v-show="loadingMessage" class="message">
                            {{ loadingMessage }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <dialog-prompt :show="dialogType === 'prompt'" />
        <dialog-info :show="dialogType === 'info'" />
        <dialog-confirm :show="dialogType === 'confirm'" />
        <dialog-alert :show="dialogType === 'alert'" />
        <context-menu :show="!!$store.getters['context/items'].length" />
        <tooltip v-if="tooltip" />

        <dialog-window :show="requiresOnboarding && isAuthRoute" :showFooter="false" width="650px">
            <template v-slot:body>
                <onboard v-if="requiresOnboarding && isAuthRoute" />
            </template>
        </dialog-window>

        <dev-tools v-if="isDev" />
        <div class="memory-stats" ref="memory-stats" v-if="isDev && memoryStats">
            {{ memoryStats }}
            <!-- 
                to use, lauch browser with memory stats enabled, eg:
                `open -a "Google Chrome" --args --enable-precise-memory-info --enable-memory-info --enable-memory-benchmarking --js-flags="--expose-gc"`
            -->
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import ContextMenu from './components/ContextMenu';
    import DevTools from './components/DevTools';
    import DialogAlert from './components/dialogs/DialogAlert';
    import DialogConfirm from './components/dialogs/DialogConfirm';
    import DialogInfo from './components/dialogs/DialogInfo';
    import DialogPrompt from './components/dialogs/DialogPrompt';
    import NavBar from './components/NavBar';
    import Onboard from './components/Onboard';
    import Tooltip from './components/Tooltip';

    export default {
        name: 'App',
        data() {
            return {
                isLoaded: false,
                memoryStats: '',
                preLoadingFocus: null,
            };
        },
        components: {
            ContextMenu,
            DevTools,
            DialogAlert,
            DialogConfirm,
            DialogInfo,
            DialogPrompt,
            NavBar,
            Onboard,
            Tooltip,
        },
        computed: {
            ...mapGetters({
                notification: 'notification/notification',
                tooltip: 'tooltip/get',
                isDragging: 'page/isDragging',
                isSelecting: 'page/isSelecting',
                showNav: 'layout/showNav',
                variables: 'layout/variables',
                dialogType: 'dialog/type',
                loading: 'base/loading',
                fullScreenLoading: 'base/fullScreenLoading',
                loadingMessage: 'base/loadingMessage',
                isAuthRoute: 'base/isAuthRoute',
                envLoaded: 'base/envLoaded',
                isDev: 'base/isDev',
            }),
            mainClasses() {
                return {
                    'is-dragging': this.isDragging,
                    'is-selecting': this.isSelecting,
                };
            },
            requiresOnboarding() {
                return this.$store.getters['user/requiresOnboarding'] && this.$route.name !== 'FillForm';
            },
        },
        async mounted() {
            if (this.isDev && typeof gc === 'function') {
                function format(num) {
                    return (num / 1024 / 1024).toFixed(1);
                }
                setInterval(() => {
                    this.memoryStats = `${format(console.memory.usedJSHeapSize)}mb/${format(
                        console.memory.totalJSHeapSize
                    )}mb`;
                }, 1000);
            }

            window.addEventListener('resize', () => {
                this.$store.commit('page/size', {
                    height: window.innerHeight,
                    width: window.innerWidth,
                });
            });

            document.addEventListener('keydown', (e) => {
                if (!this.$store.getters['page/ctrlKey'] && (e.metaKey || e.ctrlKey)) {
                    this.$store.commit('page/ctrlKey', true);
                }

                if (!this.$store.getters['page/shiftKey'] && e.shiftKey) {
                    this.$store.commit('page/shiftKey', true);
                }

                if (!this.$store.getters['page/SpaceKey'] && e.which === 32) {
                    this.$store.commit('page/spaceKey', true);
                }
            });

            document.addEventListener('keyup', (e) => {
                if (this.$store.getters['page/ctrlKey']) {
                    this.$store.commit('page/ctrlKey', false);
                }
                if (this.$store.getters['page/shiftKey']) {
                    this.$store.commit('page/shiftKey', false);
                }
                if (this.$store.getters['page/spaceKey']) {
                    this.$store.commit('page/spaceKey', false);
                }
            });
            this.isLoaded = true;
        },
        watch: {
            async envLoaded() {
                try {
                    let bundleString = await this.$api.get('d/polyfill', null, {
                        responseType: 'text',
                        supressErrors: true,
                    });
                    let script = document.createElement('script');
                    script.innerHTML = bundleString;
                    document.head.appendChild(script);
                } catch (e) {
                    // ignore error cause by redirect to login page
                    if (!/aborted/i.test(e.message)) {
                        console.error(e);
                    }
                }
            },
        },
    };
</script>

<style lang="scss">
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400..700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap');
    @import './assets/fontawesome/css/fontawesome.min.css';
    @import './assets/fontawesome/css/regular.min.css';

    #loading {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        background: $color-white;
        z-index: 1011;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .message {
            font-size: 1rem;
            margin-top: 0.5em;
            text-align: center;
        }

        &.left-bar {
            margin-left: 250px;
            width: calc(100% - 250px);
        }

        .outer {
            max-width: 300px;

            .content {
                padding: 1em;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;

                .loading-spinner {
                    opacity: 0.15;
                    width: 40px;
                    height: 40px;
                }
            }
        }

        &:not(.full-screen) {
            background: rgba(5, 10, 15, 0.1);

            .outer {
                @include border-radius-small;
                @include box-shadow-small;
                background: $color-white;
                max-width: 200px;
            }
        }
    }

    #main {
        position: relative;
        overflow-y: auto;
        flex-grow: 1;
        height: 100%;

        @media (max-width: $breakpoint-mobile) {
            height: auto;
        }

        @media print {
            height: auto;
        }

        &.is-dragging,
        &.is-selecting {
            * {
                user-select: none !important;
            }
        }
    }

    #notification {
        position: fixed;
        display: flex;
        justify-content: center;
        top: $height-nav;
        width: 100%;
        z-index: 1012;
        line-height: 20px;
        overflow: hidden;
        transition: visibility 0.5s ease-out, opacity 0.5s ease-out;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;

        &.error .inner {
            @include theme('error');
        }

        &.success .inner {
            @include theme('success');
        }

        &.info .inner {
            @include theme('info');
        }

        &.warning .inner {
            @include theme('warning');
        }

        &.active {
            opacity: 1;
            visibility: visible;

            .inner {
                transform: translateY(0);
            }
        }

        &.filler {
            top: $height-filler-header;

            @media (max-width: $breakpoint-mobile) {
                top: $height-filler-header-mobile;
            }
        }

        &:not(.show-close) .inner {
            padding-right: 24px;

            .clear-notification {
                display: none;
            }
        }

        .inner {
            transition: background-color 0.5s ease-out, transform 0.5s ease-out;
            justify-content: center;
            position: relative;
            padding-right: 45px;
            display: flex;
            align-items: center;
            margin-top: 0.5em;
            border-radius: 2em;
            max-width: 500px;
            transform: translateY(-100%);
            pointer-events: all;
        }

        .clear-notification {
            cursor: pointer;
            font-size: 18px;
            position: absolute;
            right: 10px;
            padding: 3px 6px;
            border-radius: 50%;

            &:hover {
                opacity: 0.8;
            }
        }

        > div {
            padding: 12px 24px;
        }
    }

    .memory-stats {
        @include border;
        @include border-radius-small;

        position: absolute;
        bottom: 15px;
        left: 75px;
        font-family: monospace;
        padding: 10px;
        pointer-events: none;
        z-index: 10000;
        width: 150px;
        text-align: center;
    }
</style>